import React, { useMemo, useState } from "react";
import { X } from "lucide-react";

import laxmanVideo from "./assets/testimonials/laxman.mp4";
import laxmanPoster from "./assets/testimonials/laxman_poster.jpeg";
import jhansiPhoto from "./assets/testimonials/jhansi.jpeg";

import g1 from "./assets/testimonials/teampicture.jpg";
import g2 from "./assets/testimonials/teampicture.jpg";
import g3 from "./assets/testimonials/teampicture.jpg";
import g4 from "./assets/testimonials/teampicture.jpg";
import g5 from "./assets/testimonials/teampicture.jpg";
import g6 from "./assets/testimonials/teampicture.jpg";

import kLogo from "./assets/k_logo_final.png";
import { HashLink } from "react-router-hash-link";
import Navbar from "./components/Navbar";

function Nav2() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/10 bg-black/20 backdrop-blur-md">
      <div className="w-full px-6 md:px-12 lg:px-20 py-4 flex items-center justify-between">
        <a href="/" className="flex items-center gap-3 group">
          <img
            src={kLogo}
            alt="Kurukshetra Analytics"
            className="h-8 w-auto transition duration-300 group-hover:scale-110"
          />
          <span className="font-semibold tracking-wide text-white">
            Kurukshetra Analytics
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8 text-sm text-white/80">
          <HashLink smooth to="/#showcase" className="hover:text-white transition">
            What we do
          </HashLink>
          <HashLink smooth to="/#insights" className="hover:text-white transition">
            Insights
          </HashLink>
          <a className="hover:text-white transition" href="/solutions">Our Solutions</a>
          <a className="hover:text-white transition" href="/team">Our Team</a>
          <a className="hover:text-white transition" href="/testimonials">Testimonials</a>
          <HashLink smooth to="/#getintouch" className="hover:text-white transition">
            Contact Us
          </HashLink>
        </nav>

        <a
          href="https://wa.me/919951186186"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-2xl px-4 py-2 text-white hover:opacity-90"
          style={{ fontSize: "14px" }}
        >
          📞 Call / WhatsApp: <span className="whitespace-nowrap">+91 99511 86186</span>
        </a>
      </div>
    </header>
  );
}

export default function TestimonialsPage() {
  const gallery = useMemo(
    () => [
      { src: g1, label: "Field operations" },
      { src: g2, label: "Volunteer alignment" },
      { src: g3, label: "Victory march" },
      { src: g4, label: "Booth-level coordination" },
      { src: g5, label: "War room briefing" },
      { src: g6, label: "Rapid response & comms" },
    ],
    []
  );

  // ✅ rename "open" to avoid ESLint no-restricted-globals
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [active, setActive] = useState(null);

  function openLightbox(item) {
    setActive(item);
    setLightboxOpen(true);
    document.body.style.overflow = "hidden";
  }

  function closeLightbox() {
    setLightboxOpen(false);
    setActive(null);
    document.body.style.overflow = "";
  }

  return (
    <div className="min-h-screen bg-[#0B0A17] text-white font-sans">
      <Navbar />

      {/* content spacing for fixed nav */}
      <div className="px-6 md:px-12 lg:px-20 pt-28 pb-16">
        <TestimonialSection
          gallery={gallery}
          openLightbox={openLightbox}
          lightboxOpen={lightboxOpen}
          active={active}
          closeLightbox={closeLightbox}
        />
      </div>
    </div>
  );
}

function TestimonialSection({
  gallery,
  openLightbox,
  lightboxOpen,
  active,
  closeLightbox,
}) {
  return (
    <>
      {/* PAGE HEADER */}
      <div className="max-w-4xl">
        <p className="uppercase tracking-widest text-xs text-[#A78BFA]/85">
          Trusted Voices • Field Proof
        </p>
        <h1 className="mt-3 text-4xl md:text-5xl font-semibold leading-tight">
          Testimonials & Ground Work
        </h1>
        <p className="mt-4 text-white/80 text-base md:text-lg leading-relaxed">
          We focus on execution — real field work, disciplined war rooms, and
          campaign systems that deliver.
        </p>
      </div>

      {/* SECTION 1: VIDEO BYTE */}
      <section className="mt-10">
        <div className="rounded-3xl border border-white/10 bg-white/5 overflow-hidden">
          <div className="p-6 md:p-8">
            <h2 className="text-2xl md:text-3xl font-semibold">A word from Dr. K.Laxman Garu</h2>
            <p className="mt-2 text-white/75">
              A short byte on our on-ground and strategic support.
            </p>
          </div>

          <div className="relative bg-black/40">
            <video
              className="w-full h-[260px] md:h-[420px] object-cover"
              controls
              playsInline
              preload="metadata"
              poster={laxmanPoster}
            >
              <source src={laxmanVideo} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
          </div>
        </div>
      </section>

      {/* SECTION 2: WORKED WITH */}
      <section className="mt-12">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-white/5 h-[320px] md:h-[420px]">
            <img
              src={jhansiPhoto}
              alt="Kurukshetra Analytics collaboration"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-white/90 font-semibold text-lg">
                Bothsa Jhansi Garu — campaign collaboration
              </p>
              <p className="text-white/70 text-sm mt-1">
                Constituency mapping • Ground coordination • Communications support
              </p>
            </div>
          </div>

          <div className="rounded-3xl border border-white/10 bg-white/5 p-6 md:p-8">
            <h3 className="text-2xl font-semibold">Work we’ve been trusted with</h3>
            <p className="mt-3 text-white/80 leading-relaxed">
              We operate across strategy, research, digital and field execution —
              bridging leadership intent with grassroots reality.
            </p>

            <div className="mt-6 grid gap-3">
              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm text-white/70">Typical scope</div>
                <div className="mt-1 text-white/90">
                  War room + message testing + booth planning + field reporting
                </div>
              </div>

              <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                <div className="text-sm text-white/70">How we work</div>
                <div className="mt-1 text-white/90">
                  Data-backed decisions, disciplined ops, fast execution
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 3: GALLERY */}
      <section className="mt-14">
        <h2 className="text-3xl font-semibold">Field proof</h2>
        <p className="mt-2 text-white/75">
          A glimpse of on-ground execution — planning, coordination, and delivery.
        </p>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-4">
          {gallery.map((item, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => openLightbox(item)}
              className="group relative rounded-2xl overflow-hidden border border-white/10 bg-white/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#8B5CF6]/70"
            >
              <div className="relative h-44 md:h-56">
                <img
                  src={item.src}
                  alt={item.label}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.05]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3 right-3">
                  <p className="text-sm text-white/90 font-medium">{item.label}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* LIGHTBOX */}
      {lightboxOpen && active && (
        <div
          className="fixed inset-0 z-[999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative max-w-5xl w-full rounded-3xl overflow-hidden border border-white/10 bg-[#0B0A17]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeLightbox}
              className="absolute top-3 right-3 z-10 rounded-xl border border-white/15 bg-black/30 p-2 hover:bg-black/40"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative">
              <img
                src={active.src}
                alt={active.label}
                className="w-full max-h-[80vh] object-contain bg-black"
              />
              <div className="p-4 border-t border-white/10">
                <div className="text-white/90 font-medium">{active.label}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
